import {
  Checkbox,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SxProps,
  TextField,
  Typography,
} from "@mui/material";
import { motion } from "motion/react";
import React, { useEffect, useRef, useState } from "react";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

interface MultiSelectProps {
  label: string;
  name: string;
  field: any;
  sx?: SxProps;
  required?: boolean;
  error?: string;
  icon?: React.ReactNode;
  type?: string;
  onClickIcon?: () => void;
  disabled?: boolean;
  options: { id: string; label: string }[];
  searchable?: boolean;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /** Texto cuando hay 2+ ítems (evita que el campo crezca con todos los nombres). Por defecto: "N seleccionados". */
  multipleSummary?: (count: number) => string;
  renderOption?: (option: any) => React.ReactNode;
}

const MultiSelect = ({
  label,
  name,
  field,
  sx,
  required,
  error,
  icon,
  type,
  onClickIcon,
  disabled,
  options,
  searchable = false,
  onSearchChange,
  searchPlaceholder = "Buscar...",
  multipleSummary = (count) => `${count} seleccionados`,
  renderOption,
}: MultiSelectProps) => {
  const [searchValue, setSearchValue] = useState("");
  const menuPaperRef = useRef<HTMLDivElement | null>(null);
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  useEffect(() => {
    if (menuPaperRef.current) {
      menuPaperRef.current.scrollTop = 0;
    }
  }, [searchValue]);

  const filteredOptions = searchable
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchValue.toLowerCase()),
      )
    : options;

  // Ensure field.value is always an array and filter out undefined/null values
  const safeValue = Array.isArray(field.value)
    ? field.value.filter((v: any) => v !== undefined && v !== null)
    : [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ maxWidth: "100%" }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Select
        label={""}
        labelId="demo-multiple-checkbox-label"
        id="demo-multiple-checkbox"
        multiple
        value={safeValue}
        onChange={field.onChange}
        input={<OutlinedInput label="Tag" />}
        renderValue={(selected: string[]) => {
          if (!selected.length) return "";
          if (selected.length === 1) {
            const id = selected[0];
            return options.find((option) => option.id === id)?.label ?? id;
          }
          return multipleSummary(selected.length);
        }}
        MenuProps={{
          PaperProps: {
            ref: menuPaperRef,
            style: {
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
              width: 250,
            },
          },
        }}
        sx={{
          ...sx,
          width: "100%",
          maxWidth: "100%",
          transition: "border-color 0.3s ease-in-out",
          "& .MuiSelect-select": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        }}
      >
        {searchable && (
          <MenuItem
            disableRipple
            onKeyDown={(e) => e.stopPropagation()}
            sx={{
              position: "sticky",
              top: 0,
              backgroundColor: "white",
              zIndex: 1,
            }}
          >
            <TextField
              size="small"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={handleSearchChange}
              fullWidth
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            <Checkbox checked={safeValue.includes(option.id)} />
            {renderOption ? (
              renderOption(option)
            ) : (
              <ListItemText primary={option.label} />
            )}{" "}
          </MenuItem>
        ))}
      </Select>
    </motion.div>
  );
};

export default MultiSelect;
